import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, SparklesIcon, CheckIcon, MapPinIcon, GaugeIcon, CalendarIcon, SettingsIcon, UsersIcon, FuelIcon, ShieldCheckIcon, MonitorIcon, CpuIcon, MemoryStickIcon } from 'lucide-react';
import { MerchantBadge } from '../MerchantBadge';
import { AskAIButton } from '../AskAIButton';
import { merchantFor, LISTING_SELLERS } from '../../data/merchants';
import { ListingDetailModal } from '../ListingDetailModal';
import { useAuth } from '../AuthContext';
import type { ListingCardProps } from '../ListingCard';

/** The instrument a given spec belongs to — a car reads as a dashboard. */
const SPEC_ICONS: Record<string, React.ElementType> = {
  Year: CalendarIcon,
  Mileage: GaugeIcon,
  Specs: ShieldCheckIcon,
  Color: FuelIcon,
  Transmission: SettingsIcon,
  Seats: UsersIcon,
  Engine: SettingsIcon,
  // Electronics reuse this spec-led card.
  Graphics: MonitorIcon,
  Processor: CpuIcon,
  Memory: MemoryStickIcon
};

/**
 * A car is chosen on condition, provenance and seller — so the price sits on
 * the photograph where it belongs, the specs read as a single dashboard strip
 * rather than a grid of grey boxes, and the dealer is named on the image so
 * two otherwise identical listings are distinguishable at a glance.
 */
export function VehicleCard(props: ListingCardProps) {
  const {
    id,
    title,
    price,
    location,
    images,
    specs,
    matchScore,
    selected = false,
    onToggleSelect
  } = props;
  const {
    requireAuth
  } = useAuth();
  const [frame, setFrame] = useState(0);
  const [details, setDetails] = useState(false);
  const merchant = merchantFor(LISTING_SELLERS[id] ?? location, props.sector === 'electronics' ? 'Electronics store' : props.sellerType === 'Individual' ? 'Private seller' : 'Car dealer');
  const step = (dir: 1 | -1) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setFrame(f => (f + dir + images.length) % images.length);
  };
  /** Rental stock prices per period; a sale price stands alone. */
  const [amount, period] = price.split('/');
  return <motion.div whileHover={{
    y: -4
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className={`transform-gpu bg-white rounded-2xl border overflow-hidden shadow-soft hover:shadow-medium transition-shadow duration-200 ease-out flex flex-col h-full group ${selected ? 'border-primary-indigo ring-2 ring-primary-indigo/25' : 'border-slate-200'}`} data-id="element-4927" data-mp="23q1v82u63c9v">
      <div className="relative aspect-square overflow-hidden bg-slate-900" data-id="element-4928" data-mp="1kz11pubtg8xp">
        <img src={images[frame]} alt={title} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" data-id="element-4929" data-mp="1g0hdfn9r2l26" />
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/80 via-black/25 to-transparent" data-id="element-4930" data-mp="1c0p1d0l5dws2" />

        {/* Who is selling it — the deciding factor between identical cars */}
        <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2" data-id="element-4931" data-mp="3iph8lx7o28zj">
          {matchScore > 0 ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dark-navy/75 backdrop-blur-sm text-[10.5px] font-bold text-white flex-shrink-0" data-id="element-4932" data-mp="3rnb455xlwyln">
              <SparklesIcon className="w-3 h-3" data-id="element-4933" data-mp="f5nmb03gxxm" />
              {matchScore}%
            </span> : <span data-id="element-4934" data-mp="33y8dj8kzvvf0" />}
          <MerchantBadge merchant={merchant} variant="frosted" data-id="element-4935" data-mp="33xgusychxs65" />
        </div>

        {/* Price belongs on the car, at the size people actually read it */}
        <div className="absolute bottom-2 left-2.5 right-2.5 flex items-end justify-between gap-3" data-id="element-4936" data-mp="38vi8f3r5kza8">
          <div className="min-w-0" data-id="element-4937" data-mp="3dpwcrvnsmgr1">
            <p className="font-display font-bold text-[20px] leading-none text-white" data-id="element-4938" data-mp="2g9fn1rn56frs">
              {amount}
              {period && <span className="text-[12px] font-semibold text-white/70" data-id="element-4939" data-mp="2l5xsktqaqi56">
                  /{period}
                </span>}
            </p>
            <p className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-white/70 mt-1" data-id="element-4940" data-mp="mqq7a3j8fo7a">
              <MapPinIcon className="w-3 h-3" data-id="element-4941" data-mp="10idg12ud2p6y" />
              {location}
            </p>
          </div>
          {images.length > 1 && <div className="flex gap-1 flex-shrink-0 pb-1" data-id="element-4942" data-mp="voinrb0ox1w9">
              {images.map((_, i) => <span key={i} className={`h-1 rounded-full transition-all duration-200 ease-out ${i === frame ? 'w-4 bg-white' : 'w-1 bg-white/50'}`} data-id="element-4943" data-mp="1ahm79nxudppm" data-mpi={i} />)}
            </div>}
        </div>

        {images.length > 1 && <>
            <button onClick={step(-1)} aria-label="Previous photo" className="absolute left-2 top-[42%] -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity duration-150 ease-out" data-id="element-4944" data-mp="1fcrs0jficyc4">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-4945" data-mp="3b3vuvh8x1knt" />
            </button>
            <button onClick={step(1)} aria-label="Next photo" className="absolute right-2 top-[42%] -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity duration-150 ease-out" data-id="element-4946" data-mp="2xc8w9ari401v">
              <ChevronRightIcon className="w-4 h-4" data-id="element-4947" data-mp="2sdef0d96mjjv" />
            </button>
          </>}
      </div>

      <div className="p-3 flex flex-col flex-1" data-id="element-4948" data-mp="2eeer57thu8or">
        <h3 className="font-display font-bold text-[14.5px] leading-snug text-dark-navy line-clamp-1" data-id="element-4949" data-mp="2efpwhc2l7ohz">
          {title}
        </h3>

        {/* One dashboard strip rather than a 2×2 grid of grey boxes — the same
            single meta line every other sector's card gets. */}
        {specs.length > 0 && <div className="flex items-center gap-2.5 mt-1.5 min-w-0" data-id="element-4950" data-mp="f66gapav1at4">
            {specs.slice(0, 3).map(spec => {
          const Icon = SPEC_ICONS[spec.label] ?? ShieldCheckIcon;
          return <span key={spec.label} title={`${spec.label}: ${spec.value}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 min-w-0" data-id="element-4951" data-mp="658bi4slkz9h">
                  <Icon className="w-3 h-3 text-slate-400 flex-shrink-0" data-id="element-4952" data-mp="ytvxk2l67so3" />
                  <span className="truncate" data-id="element-4953" data-mp="twdqkhqprniq">{spec.value}</span>
                </span>;
        })}
          </div>}

        <div className="mt-auto pt-2.5" data-id="element-4954" data-mp="p2j6veb3z8jn">
          {specs[3] && <p className="text-[10.5px] text-slate-400 mb-1.5 truncate" data-id="element-4955" data-mp="k4q5cykr32pp">
              {specs[3].label} · {specs[3].value}
            </p>}
          <div className="flex items-center gap-1.5" data-id="element-4956" data-mp="1cu6unnd2q3hp">
            {onToggleSelect ? <AskAIButton selected={!!selected} onClick={onToggleSelect} className="flex-1" data-id="element-4957" data-mp="1csdi5rfs4p1s" /> : <span className="flex-1" data-id="element-4958" data-mp="1p4ocoaf35jiv" />}
            <button onClick={e => {
            e.stopPropagation();
            requireAuth(() => setDetails(true));
          }} className="px-2.5 py-2 text-[12.5px] font-semibold text-slate-500 rounded-lg hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-4959" data-mp="1u2zlchtn9nzi">
              Details
            </button>
          </div>
        </div>
      </div>

      <ListingDetailModal listing={props} open={details} onClose={() => setDetails(false)} data-id="element-4960" data-mp="2cuin6lbxmvzy" />
    </motion.div>;
}